import { l as DocContext, k as jsxRuntimeExports } from "./doenetml-a_KitGQm.js";
import React__default, { useContext, useRef } from "react";
import { r as removeJXGEventHandlers, j as jxg, g as syncVisPropValues, s as syncLayer, f as syncLabelStrokeColor, b as syncLabelMaskCssStyle, e as syncLineStrokeStyle, c as computeLabelMaskCssStyle, d as attachLabelHoverHighlight } from "./JSXGraphRenderer-BMkZb_8Q.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-Ca2HvWV4.js";
import { BoardContext, LINE_LAYER_OFFSET, VERTEX_LAYER_OFFSET } from "./graph-gLVvVe_r.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { p as pointerEventToUserCoords } from "./dragThreshold-DuSSFrIU.js";
import { a as resolveLineColor, r as resolveFillColor, b as resolveCanvasColor, d as resolveHandleColor } from "./styleColors-Cknqcr0q.js";
import { s as styleToDash } from "./styleToDash-DWb71gbd.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { a as attachLineFamilyDragHandlers } from "./lineFamilyDragHandlers-tcr76K44.js";
import { c as buildBaseAttributes } from "./buildGraphicalAttributes-C7PtyKL7.js";
import { g as getPatternFillAttributes } from "./fillPatterns-BQpfOUtY.js";
const polygon = React__default.memo(function Polygon(props) {
  let { componentIdx, id, SVs, actions, sourceOfUpdate, callAction } = useDoenetRenderer(props);
  Polygon.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  let polygonJXG = useRef(null);
  let pointsJXG = useRef([]);
  let pointCoords = useRef(null);
  const dragState = usePointerDragState();
  let previousNumVertices = useRef(null);
  let jsxPointAttributes = useRef(null);
  let vertexHandlersAttached = useRef(false);
  let createdWithBorders = useRef(true);
  const dragCoordination = {
    draggedTag: useRef(null),
    downOnTag: useRef(null)
  };
  const {
    lastPositionFromCore: lastPositionsFromCore,
    fixed,
    fixLocation
  } = useDraggableRefs(SVs, SVs.numericalVertices);
  let verticesFixed = useRef(false);
  let vertexIndicesDraggable = useRef([]);
  verticesFixed.current = !SVs.verticesDraggable || SVs.fixed || SVs.fixLocation;
  vertexIndicesDraggable.current = SVs.vertexIndicesDraggable;
  const { darkMode } = useContext(DocContext) || {};
  useBoardPointerTracking(board, dragState);
  useJSXGraphCleanup({
    objectRef: polygonJXG,
    destroy: () => deletePolygonJXG()
  });
  function createPolygonJXG() {
    if (board === null) {
      return null;
    }
    if (!(SVs.numVertices >= 2)) {
      return null;
    }
    const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
    const resolvedFillColor = SVs.filled ? resolveFillColor(SVs.selectedStyle, darkMode) : "none";
    const fillAttributes = SVs.filled && board ? getPatternFillAttributes({
      defsEl: board.renderer.defs,
      boardId: board.container.id,
      fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
      fillColor: resolvedFillColor,
      fillOpacity: SVs.selectedStyle.fillOpacity,
      canvasColor: resolveCanvasColor(darkMode),
      fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity,
      highlightFillOpacity: SVs.selectedStyle.fillOpacity * 0.5
    }) : {
      fillColor: resolvedFillColor,
      fillOpacity: SVs.selectedStyle.fillOpacity,
      highlightFillColor: resolvedFillColor,
      highlightFillOpacity: SVs.selectedStyle.fillOpacity * 0.5
    };
    jsxPointAttributes.current = {
      fillColor: "none",
      strokeColor: "none",
      highlightStrokeColor: "none",
      highlightFillColor: resolveHandleColor(darkMode),
      visible: !verticesFixed.current && !SVs.hidden,
      withLabel: false,
      layer: 10 * SVs.layer + VERTEX_LAYER_OFFSET,
      highlight: true,
      showInfoBox: SVs.showCoordsWhenDragging
    };
    let jsxBorderAttributes = {
      highlight: false,
      visible: !SVs.hidden,
      layer: 10 * SVs.layer + LINE_LAYER_OFFSET,
      fixed: true,
      strokeColor: lineColor,
      strokeOpacity: SVs.selectedStyle.lineOpacity,
      highlightStrokeColor: lineColor,
      highlightStrokeOpacity: SVs.selectedStyle.lineOpacity * 0.5,
      strokeWidth: SVs.selectedStyle.lineWidth,
      highlightStrokeWidth: SVs.selectedStyle.lineWidth,
      dash: styleToDash(SVs.selectedStyle.lineStyle)
    };
    const needBorders = SVs.selectedStyle.lineWidth > 0;
    let jsxPolygonAttributes = {
      ...buildBaseAttributes({
        SVs,
        layerOffset: LINE_LAYER_OFFSET,
        fixed: fixed.current,
        fixLocation: fixLocation.current
      }),
      //specific to polygon
      fillColor: fillAttributes.fillColor,
      fillOpacity: fillAttributes.fillOpacity,
      highlightFillColor: fillAttributes.highlightFillColor,
      highlightFillOpacity: fillAttributes.highlightFillOpacity,
      vertices: jsxPointAttributes.current,
      borders: jsxBorderAttributes,
      withLines: needBorders
    };
    jsxPolygonAttributes.label = {
      highlight: false,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      highlightStrokeOpacity: 1
    };
    if (SVs.labelHasLatex) {
      jsxPolygonAttributes.label.useMathJax = true;
    }
    if (SVs.applyStyleToLabel) {
      jsxPolygonAttributes.label.strokeColor = lineColor;
    } else {
      jsxPolygonAttributes.label.strokeColor = "var(--canvasText)";
    }
    if (SVs.filled) {
      jsxPolygonAttributes.hasInnerPoints = true;
    }
    board.suspendUpdate();
    pointsJXG.current = [];
    for (let [ind, p] of SVs.numericalVertices.entries()) {
      let pointAttributes = { ...jsxPointAttributes.current };
      if (!vertexIndicesDraggable.current.includes(ind)) {
        pointAttributes.visible = false;
      }
      pointsJXG.current.push(
        board.create("point", [...p], pointAttributes)
      );
    }
    let newPolygonJXG = board.create(
      "polygon",
      pointsJXG.current,
      jsxPolygonAttributes
    );
    newPolygonJXG.isDraggable = !fixLocation.current;
    createdWithBorders.current = needBorders;
    attachLabelHoverHighlight({
      hoverTargetJXG: newPolygonJXG,
      getLabelJXG: () => polygonJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    if (!verticesFixed.current) {
      initializePoints(newPolygonJXG);
      vertexHandlersAttached.current = true;
    } else {
      vertexHandlersAttached.current = false;
    }
    attachPolygonBodyDragHandlers(newPolygonJXG);
    newPolygonJXG.on("over", () => {
      highlightVertices();
    });
    newPolygonJXG.on("out", () => {
      unHighlightVertices();
    });
    board.unsuspendUpdate();
    previousNumVertices.current = SVs.numVertices;
    return newPolygonJXG;
  }
  function initializePoints(polygon2) {
    for (let i = 0; i < SVs.numVertices; i++) {
      let vertex = polygon2.vertices[i];
      removeJXGEventHandlers(vertex);
      attachPolygonVertexDragHandlers(vertex, i);
    }
  }
  function attachPolygonBodyDragHandlers(polygon2) {
    attachLineFamilyDragHandlers({
      jxg: polygon2,
      tag: -1,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      participatesInDownTag: false,
      suppressClickWhenDownOnOtherTag: true,
      suppressFocusOnDownWhenDownOnOtherTag: true,
      actions: {
        move: actions.movePolygon,
        focus: actions.polygonFocused,
        click: actions.polygonClicked
      },
      snapshot: () => polygon2.vertices.map(
        (x) => [...x.coords.scrCoords]
      ),
      buildTransientMoveArgs: (e, snap) => {
        if (!polygonJXG.current || !board) {
          return null;
        }
        const next = [];
        if (e.type === "pointermove" && dragState.pointerAtDown.current && snap) {
          for (let j = 0; j < polygon2.vertices.length - 1; j++) {
            next.push(
              pointerEventToUserCoords(
                e,
                dragState.pointerAtDown.current,
                snap[j],
                board
              )
            );
          }
        } else {
          for (let j = 0; j < polygon2.vertices.length - 1; j++) {
            const v = polygon2.vertices[j];
            next.push([v.X(), v.Y()]);
          }
        }
        pointCoords.current = next;
        return {
          pointCoords: next,
          transient: true,
          skippable: true
        };
      },
      buildCommitMoveArgs: () => pointCoords.current ? { pointCoords: pointCoords.current } : null,
      onDragApplied: () => {
        if (!polygonJXG.current || dragCoordination.draggedTag.current !== -1) {
          return;
        }
        for (let j = 0; j < SVs.numVertices; j++) {
          polygon2.vertices[j].coords.setCoordinates(
            jxg.COORDS_BY_USER,
            [...lastPositionsFromCore.current[j]]
          );
        }
      },
      onHitExtra: () => highlightVertices(),
      onKeyFocusOutExtra: () => unHighlightVertices()
    });
  }
  function attachPolygonVertexDragHandlers(vertex, i) {
    attachLineFamilyDragHandlers({
      jxg: vertex,
      tag: i,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      shouldDispatchFocusOnDown: () => !verticesFixed.current,
      actions: {
        move: actions.movePolygon,
        focus: actions.polygonFocused,
        click: actions.polygonClicked
      },
      snapshot: () => null,
      buildTransientMoveArgs: () => {
        const map = {};
        map[i] = [vertex.X(), vertex.Y()];
        pointCoords.current = map;
        return {
          pointCoords: map,
          transient: true,
          skippable: true,
          sourceDetails: { vertex: i }
        };
      },
      buildCommitMoveArgs: (_, variant) => {
        if (!pointCoords.current) {
          return null;
        }
        if (variant === "up") {
          return {
            pointCoords: pointCoords.current,
            sourceDetails: { vertex: i }
          };
        }
        return {
          pointCoords: pointCoords.current,
          sourceInformation: { vertex: i }
        };
      },
      onDragApplied: () => {
        if (!polygonJXG.current || !board || dragCoordination.draggedTag.current !== i) {
          return;
        }
        polygonJXG.current.vertices[i].coords.setCoordinates(
          jxg.COORDS_BY_USER,
          [...lastPositionsFromCore.current[i]]
        );
        board.updateInfobox(polygonJXG.current.vertices[i]);
      },
      onHitExtra: () => highlightVertices(),
      onKeyFocusOutExtra: () => unHighlightVertices()
    });
  }
  function deletePolygonJXG() {
    if (!polygonJXG.current) {
      return;
    }
    vertexHandlersAttached.current = false;
    for (let i = 0; i < SVs.numVertices; i++) {
      let vertex = polygonJXG.current.vertices[i];
      if (vertex) {
        removeJXGEventHandlers(vertex);
      }
    }
    removeJXGEventHandlers(polygonJXG.current);
    board?.removeObject(polygonJXG.current);
    polygonJXG.current = null;
    for (const pt of pointsJXG.current) {
      board?.removeObject(pt);
    }
    pointsJXG.current = [];
  }
  function highlightVertices() {
    if (!verticesFixed.current && polygonJXG.current) {
      for (let [i, vertex] of polygonJXG.current.vertices.entries()) {
        if (vertexIndicesDraggable.current.includes(i)) {
          vertex.setAttribute({ fillcolor: "var(--canvasText)" });
          vertex.needsUpdate = true;
          vertex.update();
        }
      }
    }
  }
  function unHighlightVertices() {
    if (!verticesFixed.current && polygonJXG.current) {
      for (let [i, vertex] of polygonJXG.current.vertices.entries()) {
        if (vertexIndicesDraggable.current.includes(i)) {
          vertex.setAttribute({ fillcolor: "none" });
          vertex.needsUpdate = true;
          vertex.update();
        }
      }
    }
  }
  if (board) {
    if (!polygonJXG.current) {
      polygonJXG.current = createPolygonJXG();
    } else if (!(SVs.numVertices >= 2)) {
      deletePolygonJXG();
    } else if (SVs.selectedStyle.lineWidth > 0 !== createdWithBorders.current) {
      deletePolygonJXG();
      polygonJXG.current = createPolygonJXG();
    } else {
      let validCoords = true;
      for (let coords of SVs.numericalVertices) {
        if (!Number.isFinite(coords[0])) {
          validCoords = false;
        }
        if (!Number.isFinite(coords[1])) {
          validCoords = false;
        }
      }
      const handleColor = resolveHandleColor(darkMode);
      jsxPointAttributes.current.highlightFillColor = handleColor;
      if (previousNumVertices.current !== null && SVs.numVertices > previousNumVertices.current) {
        for (let i = previousNumVertices.current; i < SVs.numVertices; i++) {
          let pointAttributes = { ...jsxPointAttributes.current };
          if (!vertexIndicesDraggable.current.includes(i)) {
            pointAttributes.visible = false;
          }
          let newPoint = board.create(
            "point",
            [...SVs.numericalVertices[i]],
            pointAttributes
          );
          polygonJXG.current.addPoints(newPoint);
          pointsJXG.current.push(newPoint);
        }
        vertexHandlersAttached.current = false;
      } else if (previousNumVertices.current !== null && SVs.numVertices < previousNumVertices.current) {
        for (let i = previousNumVertices.current - 1; i >= SVs.numVertices; i--) {
          removeJXGEventHandlers(polygonJXG.current.vertices[i]);
          polygonJXG.current.removePoints(
            polygonJXG.current.vertices[i]
          );
          const pointToDelete = pointsJXG.current.pop();
          if (pointToDelete) {
            board?.removeObject(pointToDelete);
          }
        }
        vertexHandlersAttached.current = false;
      }
      if (!verticesFixed.current && !vertexHandlersAttached.current) {
        initializePoints(polygonJXG.current);
        vertexHandlersAttached.current = true;
      }
      let verticesVisible = !verticesFixed.current && !SVs.hidden;
      for (let i = 0; i < SVs.numVertices; i++) {
        polygonJXG.current.vertices[i].coords.setCoordinates(
          jxg.COORDS_BY_USER,
          [...SVs.numericalVertices[i]]
        );
        polygonJXG.current.vertices[i].needsUpdate = true;
        polygonJXG.current.vertices[i].update();
        let vertexVisible = verticesVisible && vertexIndicesDraggable.current.includes(i);
        syncVisPropValues(polygonJXG.current.vertices[i], {
          highlightfillcolor: handleColor
        });
        polygonJXG.current.vertices[i].visProp["visible"] = vertexVisible;
        polygonJXG.current.vertices[i].visPropCalc["visible"] = vertexVisible;
        polygonJXG.current.vertices[i].visProp.showinfobox = SVs.showCoordsWhenDragging;
      }
      if (sourceOfUpdate.sourceInformation && componentIdx in sourceOfUpdate.sourceInformation) {
        let sourceInfo = sourceOfUpdate.sourceInformation[componentIdx];
        let ind = typeof sourceInfo === "object" && sourceInfo ? sourceInfo.vertex : void 0;
        if (ind !== void 0) {
          board.updateInfobox(polygonJXG.current.vertices[ind]);
        }
      }
      let visibleNow = !SVs.hidden;
      if (!validCoords) {
        visibleNow = false;
      }
      polygonJXG.current.visProp.fixed = fixed.current;
      polygonJXG.current.visProp.highlight = !fixLocation.current;
      polygonJXG.current.isDraggable = !fixLocation.current;
      polygonJXG.current.visProp["visible"] = visibleNow;
      polygonJXG.current.visPropCalc["visible"] = visibleNow;
      let borderLayer;
      let pointLayer;
      let layerChanged = syncLayer(
        polygonJXG.current,
        SVs.layer,
        LINE_LAYER_OFFSET
      );
      if (layerChanged) {
        borderLayer = 10 * SVs.layer + LINE_LAYER_OFFSET;
        pointLayer = 10 * SVs.layer + VERTEX_LAYER_OFFSET;
      }
      const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
      const resolvedFillColor = SVs.filled ? resolveFillColor(SVs.selectedStyle, darkMode) : "none";
      const fillAttributes = SVs.filled && board ? getPatternFillAttributes({
        defsEl: board.renderer.defs,
        boardId: board.container.id,
        fillStyle: SVs.selectedStyle.fillStyle ?? "solid",
        fillColor: resolvedFillColor,
        fillOpacity: SVs.selectedStyle.fillOpacity,
        canvasColor: resolveCanvasColor(darkMode),
        fillPatternOpacity: SVs.selectedStyle.fillPatternOpacity,
        highlightFillOpacity: SVs.selectedStyle.fillOpacity * 0.5
      }) : {
        fillColor: resolvedFillColor,
        fillOpacity: SVs.selectedStyle.fillOpacity,
        highlightFillColor: resolvedFillColor,
        highlightFillOpacity: SVs.selectedStyle.fillOpacity * 0.5
      };
      polygonJXG.current.name = SVs.labelForGraph;
      if (polygonJXG.current.hasLabel && polygonJXG.current.label) {
        const label = polygonJXG.current.label;
        syncLabelStrokeColor(label, SVs.applyStyleToLabel, lineColor);
        syncLabelMaskCssStyle(label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        label.needsUpdate = true;
        label.update();
      }
      if (polygonJXG.current.visProp.fillcolor !== fillAttributes.fillColor) {
        polygonJXG.current.visProp.fillcolor = fillAttributes.fillColor;
        polygonJXG.current.visProp.highlightfillcolor = fillAttributes.highlightFillColor;
        polygonJXG.current.visProp.hasinnerpoints = SVs.filled;
      }
      if (polygonJXG.current.visProp.fillopacity !== fillAttributes.fillOpacity) {
        polygonJXG.current.visProp.fillopacity = fillAttributes.fillOpacity;
        polygonJXG.current.visProp.highlightfillopacity = fillAttributes.highlightFillOpacity;
      }
      polygonJXG.current.needsUpdate = true;
      polygonJXG.current.update();
      polygonJXG.current.updateVisibility();
      for (let i = 0; i < polygonJXG.current.borders.length; i++) {
        let border = polygonJXG.current.borders[i];
        border.visProp.visible = visibleNow;
        border.visPropCalc.visible = visibleNow;
        if (layerChanged && borderLayer !== void 0) {
          border.setAttribute({ layer: borderLayer });
        }
        syncLineStrokeStyle(border, {
          lineColor,
          lineWidth: SVs.selectedStyle.lineWidth,
          lineOpacity: SVs.selectedStyle.lineOpacity,
          dash: styleToDash(SVs.selectedStyle.lineStyle)
        });
        border.needsUpdate = true;
        border.update();
      }
      if (layerChanged && pointLayer !== void 0) {
        jsxPointAttributes.current.layer = pointLayer;
        for (let vertex of polygonJXG.current.vertices) {
          vertex.setAttribute({ layer: pointLayer });
          vertex.needsUpdate = true;
          vertex.update();
        }
      }
      previousNumVertices.current = SVs.numVertices;
      board.updateRenderer();
    }
  }
  if (SVs.hidden) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
});
export {
  polygon as default
};
